/**
 * RadioGroupExamples.tsx
 * Comprehensive examples of HeroUI RadioGroup and Radio components
 * 
 * Showcases all HeroUI RadioGroup features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { RadioGroup, Radio } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { 
  Cpu,
  Database,
  Globe,
  Mail,
  Shield,
  Smartphone,
  Star,
  Users
} from 'lucide-react';

export const RadioGroupExamples: React.FC = () => {
  // Basic radio group states
  const [basicValue, setBasicValue] = useState<string>('option1');
  const [colorValues, setColorValues] = useState({
    default: 'option1',
    primary: 'option2',
    secondary: 'option1',
    success: 'option3',
    warning: 'option2',
    danger: 'option1'
  });
  const [sizeValues, setSizeValues] = useState({
    sm: 'small1',
    md: 'medium2',
    lg: 'large1'
  });
  const [orientationValues, setOrientationValues] = useState({
    vertical: 'vert1',
    horizontal: 'horiz2'
  });
  const [stateValues, setStateValues] = useState({
    required: '',
    invalid: 'bad',
    disabled: 'option1'
  });
  const [advancedValues, setAdvancedValues] = useState({
    description: 'plan1',
    controlled: 'controlled2',
    notifications: 'email',
    datasource: 'cloud'
  });

  const handleColorChange = (color: keyof typeof colorValues) => (value: string) => {
    setColorValues(prev => ({ ...prev, [color]: value }));
  };

  const handleSizeChange = (size: keyof typeof sizeValues) => (value: string) => {
    setSizeValues(prev => ({ ...prev, [size]: value }));
  };

  const handleOrientationChange = (orientation: keyof typeof orientationValues) => (value: string) => {
    setOrientationValues(prev => ({ ...prev, [orientation]: value }));
  };

  const handleStateChange = (state: keyof typeof stateValues) => (value: string) => {
    setStateValues(prev => ({ ...prev, [state]: value }));
  };

  const handleAdvancedChange = (type: keyof typeof advancedValues) => (value: string) => {
    setAdvancedValues(prev => ({ ...prev, [type]: value }));
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Basic Usage</h3>
            <p className="text-text-secondary">Simple radio group with basic options</p>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <RadioGroup 
                  label="Choose an option"
                  value={basicValue}
                  onValueChange={setBasicValue}
                >
                  <Radio value="option1">Option 1</Radio>
                  <Radio value="option2">Option 2</Radio>
                  <Radio value="option3">Option 3</Radio>
                </RadioGroup>
              </div>
              <div className="text-sm text-text-secondary">
                <Badge>Selected: {basicValue}</Badge>
                <p className="mt-2">Basic radio group with string values</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Color Variants */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Color Variants</h3>
            <p className="text-text-secondary">RadioGroup components with different color themes</p>
          </CardHeader>
          <CardBody>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(['default', 'primary', 'secondary', 'success', 'warning', 'danger'] as const).map((color) => (
                <div key={color}>
                  <RadioGroup
                    label={`${color.charAt(0).toUpperCase() + color.slice(1)} Theme`}
                    color={color}
                    value={colorValues[color]}
                    onValueChange={handleColorChange(color)}
                    className="mb-2"
                  >
                    <Radio value="option1">Option 1</Radio>
                    <Radio value="option2">Option 2</Radio>
                    <Radio value="option3">Option 3</Radio>
                  </RadioGroup>
                  <Badge variant="flat" color={color === 'default' ? 'primary' : color}>
                    {colorValues[color]}
                  </Badge>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Size Variants */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Size Variants</h3>
            <p className="text-text-secondary">RadioGroup components in different sizes</p>
          </CardHeader>
          <CardBody>
            <div className="grid md:grid-cols-3 gap-6">
              {(['sm', 'md', 'lg'] as const).map((size) => (
                <div key={size}>
                  <RadioGroup
                    label={`Size ${size.toUpperCase()}`}
                    size={size}
                    color="primary"
                    value={sizeValues[size]}
                    onValueChange={handleSizeChange(size)}
                    className="mb-2"
                  >
                    <Radio value={`${size}1`}>Small option</Radio>
                    <Radio value={`${size}2`}>Medium length option</Radio>
                    <Radio value={`${size}3`}>Very long option with more text</Radio>
                  </RadioGroup>
                  <Badge>{sizeValues[size]}</Badge>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Orientation */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Orientation</h3>
            <p className="text-text-secondary">Vertical and horizontal radio group layouts</p>
          </CardHeader>
          <CardBody>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <RadioGroup
                  label="Vertical Layout (Default)"
                  orientation="vertical"
                  color="primary"
                  value={orientationValues.vertical}
                  onValueChange={handleOrientationChange('vertical')}
                  className="mb-2"
                >
                  <Radio value="vert1">First option</Radio>
                  <Radio value="vert2">Second option</Radio>
                  <Radio value="vert3">Third option</Radio>
                </RadioGroup>
                <Badge>Selected: {orientationValues.vertical}</Badge>
              </div>
              <div>
                <RadioGroup
                  label="Horizontal Layout"
                  orientation="horizontal"
                  color="secondary"
                  value={orientationValues.horizontal}
                  onValueChange={handleOrientationChange('horizontal')}
                  className="mb-2"
                >
                  <Radio value="horiz1">Option A</Radio>
                  <Radio value="horiz2">Option B</Radio>
                  <Radio value="horiz3">Option C</Radio>
                </RadioGroup>
                <Badge color="secondary">Selected: {orientationValues.horizontal}</Badge>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* States */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Component States</h3>
            <p className="text-text-secondary">Required, invalid, and disabled states</p>
          </CardHeader>
          <CardBody>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <RadioGroup
                  label="Required Field"
                  color="primary"
                  isRequired
                  value={stateValues.required}
                  onValueChange={handleStateChange('required')}
                  className="mb-2"
                >
                  <Radio value="req1">First choice</Radio>
                  <Radio value="req2">Second choice</Radio>
                </RadioGroup>
                <Badge color={stateValues.required ? 'success' : 'warning'}>
                  {stateValues.required || 'None selected'}
                </Badge>
              </div>
              <div>
                <RadioGroup
                  label="Invalid State"
                  color="danger"
                  isInvalid
                  errorMessage="Please select a valid option"
                  value={stateValues.invalid}
                  onValueChange={handleStateChange('invalid')}
                  className="mb-2"
                >
                  <Radio value="good">Good option</Radio>
                  <Radio value="bad">Bad option</Radio>
                </RadioGroup>
                <Badge color="danger">Selected: {stateValues.invalid}</Badge>
              </div>
              <div>
                <RadioGroup
                  label="Disabled Group"
                  isDisabled
                  value={stateValues.disabled}
                  onValueChange={handleStateChange('disabled')}
                  className="mb-2"
                >
                  <Radio value="option1">Disabled option 1</Radio>
                  <Radio value="option2">Disabled option 2</Radio>
                </RadioGroup>
                <Badge variant="flat">Disabled</Badge>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Advanced Features */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Advanced Features</h3>
            <p className="text-text-secondary">Description text and controlled components</p>
          </CardHeader>
          <CardBody>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <RadioGroup
                  label="Choose Your Plan"
                  description="Select the plan that best fits your needs"
                  color="primary"
                  value={advancedValues.description}
                  onValueChange={handleAdvancedChange('description')}
                  className="mb-4"
                >
                  <Radio value="plan1" description="Perfect for individuals">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Starter Plan
                    </div>
                  </Radio>
                  <Radio value="plan2" description="Great for small teams">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4" />
                      Pro Plan
                    </div>
                  </Radio>
                  <Radio value="plan3" description="Enterprise-grade solution">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Enterprise Plan
                    </div>
                  </Radio>
                </RadioGroup>
                <Badge color="primary">Selected: {advancedValues.description}</Badge>
              </div>
              <div>
                <RadioGroup
                  label="Controlled Component"
                  color="secondary"
                  value={advancedValues.controlled}
                  onValueChange={handleAdvancedChange('controlled')}
                  className="mb-4"
                >
                  <Radio value="controlled1">Controlled Option 1</Radio>
                  <Radio value="controlled2">Controlled Option 2</Radio>
                  <Radio value="controlled3">Controlled Option 3</Radio>
                </RadioGroup>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onPress={() => handleAdvancedChange('controlled')('controlled1')}
                  >
                    Set Option 1
                  </Button>
                  <Button 
                    size="sm" 
                    variant="bordered"
                    onPress={() => handleAdvancedChange('controlled')('')}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Real-world Examples */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Real-world Examples</h3>
            <p className="text-text-secondary">Practical use cases for RadioGroup components</p>
          </CardHeader>
          <CardBody>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <RadioGroup
                  label="Notification Preferences"
                  color="primary"
                  value={advancedValues.notifications}
                  onValueChange={handleAdvancedChange('notifications')}
                  className="mb-4"
                >
                  <Radio value="email">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email notifications
                    </div>
                  </Radio>
                  <Radio value="push">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4" />
                      Push notifications
                    </div>
                  </Radio>
                  <Radio value="none">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      No notifications
                    </div>
                  </Radio>
                </RadioGroup>
              </div>
              <div>
                <RadioGroup
                  label="Data Source"
                  color="success"
                  value={advancedValues.datasource}
                  onValueChange={handleAdvancedChange('datasource')}
                  className="mb-4"
                >
                  <Radio value="cloud">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Cloud Storage
                    </div>
                  </Radio>
                  <Radio value="local">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Local Database
                    </div>
                  </Radio>
                  <Radio value="hybrid">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Hybrid Approach
                    </div>
                  </Radio>
                </RadioGroup>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};